import React, { useState, useEffect, useMemo } from 'react';
import { 
  getMonthMatrix, 
  getScheduleForDate, 
  TIME_SLOTS,
  formatFullDate,
  isSameDay
} from '../../utils/calendarUtils';
import {
  Syringe,
  Flame,
  Pill,
  Sun,
  SunMedium,
  Moon,
  ExternalLink,
  CalendarCheck
} from 'lucide-react';
import { normalizeCompoundCategory, formatDosageRange } from '../../utils/sortUtils';

export default function MonthView({
  currentDate,
  protocols = [],
  statusFilter = 'Active',
  categoryFilter = 'All',
  searchQuery = '',
  weekStart = 'sun',
  onSelectDay
}) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Mobile selected date for agenda preview below the grid
  const [selectedMobileDate, setSelectedMobileDate] = useState(currentDate);

  useEffect(() => {
    setSelectedMobileDate(currentDate);
  }, [currentDate]);

  const monthMatrix = useMemo(() => {
    return getMonthMatrix(year, month, weekStart);
  }, [year, month, weekStart]);

  // Compute schedules for each cell in month
  const cellsWithSchedule = useMemo(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    const searchableProtocols = searchLower
      ? protocols.filter(p => 
          (p.name || '').toLowerCase().includes(searchLower) ||
          (p.intendedOutcome || '').toLowerCase().includes(searchLower) ||
          (p.notes || '').toLowerCase().includes(searchLower)
        )
      : protocols;

    return monthMatrix.map(cell => {
      const schedule = getScheduleForDate(searchableProtocols, cell.date, {
        statusFilter,
        categoryFilter
      });
      return {
        ...cell,
        schedule
      };
    });
  }, [monthMatrix, protocols, statusFilter, categoryFilter, searchQuery]);

  // Schedule for the active mobile selected date
  const mobileSchedule = useMemo(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    const searchableProtocols = searchLower
      ? protocols.filter(p => 
          (p.name || '').toLowerCase().includes(searchLower) ||
          (p.intendedOutcome || '').toLowerCase().includes(searchLower) ||
          (p.notes || '').toLowerCase().includes(searchLower)
        )
      : protocols;

    return getScheduleForDate(searchableProtocols, selectedMobileDate, {
      statusFilter,
      categoryFilter
    });
  }, [selectedMobileDate, protocols, statusFilter, categoryFilter, searchQuery]);

  const weekdayLabels = weekStart === 'mon'
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getCategoryTheme = (category) => {
    const cat = normalizeCompoundCategory(category);
    switch (cat) {
      case 'Peptide':
        return {
          dot: 'bg-purple-400',
          badge: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
          icon: Syringe
        };
      case 'Hormones & AAS':
        return {
          dot: 'bg-rose-400',
          badge: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
          icon: Flame
        };
      case 'Supplement':
      default:
        return {
          dot: 'bg-sky-400',
          badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
          icon: Pill
        };
    }
  };

  const getSlotIcon = (slotId) => {
    switch (slotId) {
      case 'AM': return Sun;
      case 'NOON': return SunMedium;
      case 'BEDTIME': return Moon;
      default: return Sun;
    }
  };

  return (
    <div className="space-y-4">
      {/* Month Calendar Card */}
      <div className="space-y-2">
        {/* Weekday Header */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center text-[11px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider py-2 border-b border-slate-800">
          {weekdayLabels.map((day, idx) => {
            const isWeekend = weekStart === 'mon' ? idx >= 5 : (idx === 0 || idx === 6);
            return (
              <div key={day} className={isWeekend ? 'text-slate-500' : ''}>
                {day}
              </div>
            );
          })}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {cellsWithSchedule.map((cell) => {
            const { schedule, isCurrentMonth, isToday, dayNumber, dateString } = cell;
            const totalScheduled = schedule.totalScheduled;
            const amCount = schedule.bySlot.AM.length;
            const noonCount = schedule.bySlot.NOON.length;
            const bedtimeCount = schedule.bySlot.BEDTIME.length;
            const isMobileSelected = isSameDay(cell.date, selectedMobileDate);

            // Compounds preview (up to 3 - desktop only)
            const previewProtocols = schedule.protocols.slice(0, 3);
            const remainingCount = Math.max(0, schedule.protocols.length - 3);

            return (
              <div
                key={dateString}
                onClick={() => {
                  setSelectedMobileDate(cell.date);
                  // Open modal on desktop/tablet, or update mobile agenda
                  if (typeof window !== 'undefined' && window.innerWidth >= 640) {
                    onSelectDay(cell.date);
                  }
                }}
                className={`min-h-[58px] sm:min-h-[130px] p-1.5 sm:p-2.5 rounded-xl sm:rounded-2xl border transition-all cursor-pointer flex flex-col justify-between group ${
                  isMobileSelected
                    ? 'ring-2 ring-sky-400 bg-slate-900/90 border-sky-500 shadow-md shadow-sky-500/20 sm:ring-0 sm:shadow-none'
                    : ''
                } ${
                  isToday
                    ? 'bg-slate-900/90 border-sky-500/60 shadow-lg shadow-sky-500/10 sm:ring-1 sm:ring-sky-500/30'
                    : isCurrentMonth
                    ? 'bg-slate-900/40 border-slate-800/80 hover:bg-slate-900/80 hover:border-slate-700'
                    : 'bg-slate-950/20 border-slate-900/60 opacity-40 hover:opacity-80 hover:bg-slate-900/40'
                }`}
              >
                {/* Cell Header: Day Number & Count */}
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs sm:text-sm font-bold w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-lg ${
                      isToday
                        ? 'bg-sky-500 text-white shadow-sm shadow-sky-500/50'
                        : isCurrentMonth
                        ? 'text-slate-200'
                        : 'text-slate-500'
                    }`}
                  >
                    {dayNumber}
                  </span>

                  {totalScheduled > 0 && (
                    <span className="text-[9px] sm:text-[11px] font-semibold px-1 sm:px-1.5 py-0.5 rounded-md bg-slate-800/90 text-slate-300 border border-slate-700/60">
                      {totalScheduled}
                    </span>
                  )}
                </div>

                {/* Time Slot Indicators (AM, Noon, Bedtime dots) */}
                {totalScheduled > 0 && (
                  <div className="flex items-center justify-center sm:justify-start space-x-1 sm:space-x-1.5 my-0.5 sm:my-1">
                    {amCount > 0 && (
                      <span 
                        className="w-1.5 h-1.5 rounded-full bg-amber-400" 
                        title={`${amCount} Morning / AM`} 
                      />
                    )}
                    {noonCount > 0 && (
                      <span 
                        className="w-1.5 h-1.5 rounded-full bg-sky-400" 
                        title={`${noonCount} Midday / Noon`} 
                      />
                    )}
                    {bedtimeCount > 0 && (
                      <span 
                        className="w-1.5 h-1.5 rounded-full bg-indigo-400" 
                        title={`${bedtimeCount} Evening / Bedtime`} 
                      />
                    )}
                  </div>
                )}

                {/* Compound Mini Chips (Desktop / Tablet only) */}
                <div className="space-y-1 mt-auto overflow-hidden hidden sm:block">
                  {previewProtocols.map((p, idx) => (
                    <div
                      key={`${p.id || p.name}-${idx}`}
                      className="flex items-center space-x-1 px-1.5 py-0.5 rounded bg-slate-950/80 border border-slate-800/80 text-[10px] text-slate-300 truncate"
                      title={`${p.name} (${p.dosageMin}${p.unit})`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${getCategoryTheme(p.category).dot}`} />
                      <span className="truncate font-medium">{p.name}</span>
                    </div>
                  ))}

                  {remainingCount > 0 && (
                    <div className="text-[9px] font-semibold text-slate-400 pl-1">
                      +{remainingCount} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile Selected Day Agenda List (sm:hidden) */}
      <div className="sm:hidden mt-4 p-4 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-xl space-y-4">
        {/* Agenda Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-800">
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-bold text-white">
                {formatFullDate(selectedMobileDate)}
              </h3>
              {isSameDay(selectedMobileDate, new Date()) && (
                <span className="px-2 py-0.2 rounded-full text-[10px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30">
                  Today
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              {mobileSchedule.totalScheduled} {mobileSchedule.totalScheduled === 1 ? 'compound' : 'compounds'} scheduled
            </p>
          </div>

          <button
            type="button"
            onClick={() => onSelectDay(selectedMobileDate)}
            className="flex items-center space-x-1 px-3 py-1.5 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 font-semibold text-xs border border-sky-500/30 transition shrink-0"
            title="Open Full Day Details Modal"
          >
            <span>Full Details</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Agenda Content */}
        {mobileSchedule.totalScheduled === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500">
            No compounds scheduled for this date. Tap another day above.
          </div>
        ) : (
          <div className="space-y-4">
            {TIME_SLOTS.map(slot => {
              const SlotIcon = getSlotIcon(slot.id);
              const protocolsInSlot = mobileSchedule.bySlot[slot.id] || [];
              if (protocolsInSlot.length === 0) return null;

              return (
                <div key={slot.id} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className={`p-1 rounded-md ${slot.bgColor} ${slot.textColor} border ${slot.borderColor}`}>
                      <SlotIcon className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-xs font-bold text-slate-200">
                      {slot.label}
                    </span>
                    <span className="text-[10px] text-slate-500">
                      ({protocolsInSlot.length})
                    </span>
                  </div>

                  <div className="space-y-2">
                    {protocolsInSlot.map((p, idx) => {
                      const theme = getCategoryTheme(p.category);
                      const CategoryIcon = theme.icon;
                      const dosageDisplay = formatDosageRange(p);

                      return (
                        <div
                          key={`mobile-agenda-${p.id || p.name}-${idx}`}
                          onClick={() => onSelectDay(selectedMobileDate)}
                          className="p-2.5 rounded-2xl bg-slate-950/70 border border-slate-800/90 active:border-slate-700 transition flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center space-x-1.5">
                              <span className="font-bold text-xs text-white truncate">
                                {p.name}
                              </span>
                              <span className={`p-0.5 rounded text-[9px] font-semibold border ${theme.badge} shrink-0`}>
                                <CategoryIcon className="w-2.5 h-2.5 inline mr-0.5" />
                                {normalizeCompoundCategory(p.category)}
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5 flex items-center space-x-1.5">
                              <span className="font-bold text-sky-300">{dosageDisplay}</span>
                              {p.administrationFormat && (
                                <>
                                  <span>•</span>
                                  <span>{p.administrationFormat}</span>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="text-[10px] font-mono text-slate-500 shrink-0">
                            {p.frequencySchedule || 'Daily'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
